import { useEffect, useRef } from "react";

// Animated ASCII field behind the page: slowly flowing bright regions,
// a soft glow that trails the cursor, and waves of random speed and shape
// that ripple out on every click (plus an occasional ambient one).
// Every cell eases toward its target brightness, so motion stays smooth.
// The field is softer behind the main column so content stays readable.

const CHARS =
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const CELL_W = 9;
const CELL_H = 14;
const CONTENT_HALF = 440; // half of the main column width, in px
const LEVELS = 16;
const CENTER_LEVEL = 0.32; // field strength behind the main content
const GLOW_RADIUS = 220; // cursor glow radius, in px
const GLOW_STRENGTH = 1.1; // cursor glow brightness
const SHAPES = ["circle", "ellipse", "square", "diamond", "flower"];

const hexToRgb = (hex) =>
  [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

const smoothstep = (a, b, x) => {
  const t = Math.min(Math.max((x - a) / (b - a), 0), 1);
  return t * t * (3 - 2 * t);
};

const rand = (min, max) => min + Math.random() * (max - min);

function makeWave(x, y, now, opts = {}) {
  const shape = opts.shape || SHAPES[(Math.random() * SHAPES.length) | 0];
  const angle = rand(0, Math.PI);
  const stretch = shape === "ellipse" ? rand(1.4, 2.2) : rand(0.9, 1.15);
  return {
    x,
    y,
    t0: now + (opts.delay || 0),
    shape,
    speed: rand(0.28, 0.85), // px per ms
    life: rand(1500, 2800),
    width: rand(22, 58), // band thickness
    strength: (opts.strength || 1) * rand(0.75, 1.1),
    cos: Math.cos(angle),
    sin: Math.sin(angle),
    sx: stretch,
    sy: 1 / Math.sqrt(stretch),
    petals: (rand(4, 8) | 0) || 5,
    phase: rand(0, Math.PI * 2),
  };
}

// Distance from the wave center in the wave's own shape metric.
function shapeDistance(w, dx, dy) {
  const u = (dx * w.cos + dy * w.sin) / w.sx;
  const v = (-dx * w.sin + dy * w.cos) / w.sy;
  switch (w.shape) {
    case "square":
      return Math.max(Math.abs(u), Math.abs(v));
    case "diamond":
      return (Math.abs(u) + Math.abs(v)) * 0.72;
    case "flower": {
      const r = Math.sqrt(u * u + v * v);
      const a = Math.atan2(v, u);
      return r / (1 + 0.22 * Math.sin(w.petals * a + w.phase));
    }
    default:
      return Math.sqrt(u * u + v * v);
  }
}

function Background({ theme }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Matches the --muted / --accent tokens in dev.css, plus a warm accent.
    const dark = theme !== "light";
    // Colors 0-2 form the ambient field; 3-4 light up around the cursor,
    // with a higher ceiling so the glow reads clearly.
    const palette = dark
      ? [hexToRgb("#8b90a0"), hexToRgb("#7c8cff"), [224, 180, 90], hexToRgb("#9aa6ff"), hexToRgb("#3ddc97")]
      : [hexToRgb("#6b7080"), hexToRgb("#4f5bd5"), [192, 138, 30], hexToRgb("#4f5bd5"), hexToRgb("#12a36a")];
    const ambientAlpha = dark ? 0.55 : 0.6;
    const glowAlpha = dark ? 0.7 : 0.68;
    const alphas = palette.map((_, c) =>
      Array.from({ length: LEVELS }, (_, l) => ((l + 1) / LEVELS) * (c >= 3 ? glowAlpha : ambientAlpha))
    );
    let atlases = [];
    let atlasDpr = 1;

    let width = 0;
    let height = 0;
    let cols = 0;
    let rows = 0;
    let chars = new Uint8Array(0);
    let level = new Float32Array(0);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      atlasDpr = dpr;
      atlases = palette.map((rgb) => {
        const a = document.createElement("canvas");
        a.width = Math.ceil(CHARS.length * CELL_W * dpr);
        a.height = Math.ceil(CELL_H * dpr);
        const actx = a.getContext("2d");
        actx.scale(dpr, dpr);
        actx.font = "11px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
        actx.textBaseline = "top";
        actx.fillStyle = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
        for (let i = 0; i < CHARS.length; i++) actx.fillText(CHARS[i], i * CELL_W, 1);
        return a;
      });
      cols = Math.ceil(width / CELL_W);
      rows = Math.ceil(height / CELL_H);
      chars = new Uint8Array(cols * rows);
      level = new Float32Array(cols * rows);
      for (let i = 0; i < chars.length; i++) {
        chars[i] = (Math.random() * CHARS.length) | 0;
      }
    };

    const waves = [];
    const mouse = { x: -9999, y: -9999, tx: -9999, ty: -9999, on: false };
    let nextAmbient = performance.now() + rand(2500, 5000);

    const onPointerDown = (e) => {
      const now = performance.now();
      waves.push(makeWave(e.clientX, e.clientY, now));
      // A softer echo of a different shape, slightly behind.
      waves.push(
        makeWave(e.clientX, e.clientY, now, { delay: rand(90, 220), strength: 0.55 })
      );
      while (waves.length > 10) waves.shift();
    };
    const onPointerMove = (e) => {
      mouse.tx = e.clientX;
      mouse.ty = e.clientY;
      if (!mouse.on) {
        mouse.x = mouse.tx;
        mouse.y = mouse.ty;
        mouse.on = true;
      }
    };
    const onPointerLeave = () => {
      mouse.on = false;
    };

    const buckets = Array.from({ length: palette.length * LEVELS }, () => []);
    let glow = 0; // eased cursor glow strength

    const draw = (now, dt) => {
      const t = now / 1000;
      // Frame-rate independent easing factors.
      const ease = 1 - Math.exp(-dt / 140);
      const follow = 1 - Math.exp(-dt / 90);

      mouse.x += (mouse.tx - mouse.x) * follow;
      mouse.y += (mouse.ty - mouse.y) * follow;
      glow += ((mouse.on ? 1 : 0) - glow) * (1 - Math.exp(-dt / 300));

      // Occasional ambient wave out at the edges.
      if (!reduced && now > nextAmbient) {
        const side = Math.random() < 0.5 ? rand(0, 0.25) : rand(0.75, 1);
        waves.push(
          makeWave(width * side, height * rand(0.1, 0.9), now, { strength: 0.45 })
        );
        nextAmbient = now + rand(3500, 7000);
      }
      for (let i = waves.length - 1; i >= 0; i--) {
        if (now - waves[i].t0 > waves[i].life) waves.splice(i, 1);
      }

      ctx.clearRect(0, 0, width, height);
      for (let b = 0; b < buckets.length; b++) buckets[b].length = 0;

      const cx0 = width / 2;
      const half = Math.min(CONTENT_HALF, width / 2 - 16) - 30;

      for (let row = 0; row < rows; row++) {
        const py = row * CELL_H;
        const rowWarp = Math.sin(row * 0.045 + t * 0.13) * 1.6;
        for (let col = 0; col < cols; col++) {
          const px = col * CELL_W;
          const idx = row * cols + col;
          const edge = Math.min(Math.max((Math.abs(px - cx0) - half) / 140, 0), 1);

          // Slow, domain-warped flow with a drifting ring.
          const wx = col + rowWarp * 6;
          const rx = col - cols * (0.22 + 0.05 * Math.sin(t * 0.07));
          const ry = row - rows * (0.62 + 0.05 * Math.cos(t * 0.05));
          const a =
            Math.sin(wx * 0.038 + t * 0.16) +
            Math.sin(row * 0.052 - t * 0.12 + Math.sin(col * 0.02) * 1.2) +
            Math.sin((col + row) * 0.022 + t * 0.09) +
            0.9 * Math.sin(Math.sqrt(rx * rx + ry * ry) * 0.18 - t * 0.45);
          let v = 0.1 + 0.9 * smoothstep(0.45, 0.92, (a + 3.9) / 7.8);

          // Softer behind the reading column, full strength outside it.
          v *= CENTER_LEVEL + (1 - CENTER_LEVEL) * edge;

          // Cursor glow that trails the pointer.
          let near = 0;
          if (glow > 0.01) {
            const mdx = px - mouse.x;
            const mdy = py - mouse.y;
            const md2 = mdx * mdx + mdy * mdy;
            if (md2 < GLOW_RADIUS * GLOW_RADIUS) {
              const f = 1 - Math.sqrt(md2) / GLOW_RADIUS;
              near = f * glow;
              v += GLOW_STRENGTH * f * f * glow;
            }
          }

          // Waves.
          let crest = 0;
          for (let w = 0; w < waves.length; w++) {
            const wave = waves[w];
            const age = now - wave.t0;
            if (age < 0) continue;
            const p = age / wave.life;
            const radius = wave.speed * age * (1 - 0.3 * p); // eases out
            const d = shapeDistance(wave, px - wave.x, py - wave.y) - radius;
            const reach = wave.width * 2.2;
            if (d > -reach && d < reach) {
              const fadeIn = Math.min(age / 120, 1);
              const fadeOut = Math.pow(1 - p, 1.6);
              const band = Math.exp(-(d * d) / (wave.width * wave.width));
              const k = band * wave.strength * fadeIn * fadeOut;
              v += k;
              if (k > crest) crest = k;
            }
          }

          // Ease toward the target so changes never pop.
          const cur = level[idx] + (Math.min(v, 1) - level[idx]) * ease;
          level[idx] = cur;

          // Scramble characters under a passing crest, rarely otherwise.
          if (Math.random() < (crest > 0.35 ? 0.12 : near > 0.55 ? 0.05 : 0.0025)) {
            chars[idx] = (Math.random() * CHARS.length) | 0;
          }

          if (cur < 0.05) continue;
          const l = Math.min(LEVELS - 1, Math.floor(cur * LEVELS));
          const h =
            Math.sin(col * 0.021 + 1.3 + t * 0.02) * Math.cos(row * 0.033 - t * 0.04);
          const color =
            near > 0.7 ? 4 : near > 0.35 ? 3 : h > 0.42 ? 1 : h < -0.5 ? 2 : 0;
          buckets[color * LEVELS + l].push(px, py, chars[idx]);
        }
      }

      const sw = CELL_W * atlasDpr;
      const sh = CELL_H * atlasDpr;
      for (let b = 0; b < buckets.length; b++) {
        const list = buckets[b];
        if (!list.length) continue;
        const atlas = atlases[(b / LEVELS) | 0];
        ctx.globalAlpha = alphas[(b / LEVELS) | 0][b % LEVELS];
        for (let i = 0; i < list.length; i += 3) {
          ctx.drawImage(atlas, list[i + 2] * sw, 0, sw, sh, list[i], list[i + 1], CELL_W, CELL_H);
        }
      }
      ctx.globalAlpha = 1;
    };

    // Run at full frame rate; drop to ~30fps only if drawing is too slow.
    let raf = 0;
    let last = performance.now();
    let minInterval = 0;
    let slowFrames = 0;
    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      if (document.hidden) {
        last = now;
        return;
      }
      const dt = now - last;
      if (dt < minInterval) return;
      last = now;
      const start = performance.now();
      draw(now, Math.min(dt, 100));
      const cost = performance.now() - start;
      slowFrames = cost > 12 ? slowFrames + 1 : Math.max(slowFrames - 1, 0);
      if (slowFrames > 20) minInterval = 1000 / 30;
    };

    resize();
    window.addEventListener("resize", resize);
    if (reduced) {
      level.fill(0);
      draw(performance.now(), 10000);
    } else {
      window.addEventListener("pointerdown", onPointerDown, { passive: true });
      window.addEventListener("pointermove", onPointerMove, { passive: true });
      document.addEventListener("pointerleave", onPointerLeave);
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [theme]);

  return <canvas ref={canvasRef} className="bg-field" aria-hidden="true" />;
}

export default Background;
